// imageCache.js
const CACHE_NAME = 'image-cache-v1';
export const CACHE_EXPIRY = 86400 * 1000;

let onCacheExpiredCallback = null;

function toCacheKey(key) {
  try {
    new URL(key);
    return key;
  } catch {
    return `https://cache.local/${key}`;
  }
}

export async function getCachedImageOrFetch(url, stableKey = null) {
  if (!url) return null;
  const cacheKey = toCacheKey(stableKey ?? url);

  try {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(cacheKey);

    if (cached) {
      const cachedAt = cached.headers.get('x-cached-at');
      const isExpired = !cachedAt || Date.now() - Number(cachedAt) >= CACHE_EXPIRY;

      if (!isExpired) {
        const { base64, mimeType } = await cached.json();
        return { blobUrl: base64ToBlob(base64, mimeType), expired: false };
      }

      // Entry expired — wipe entire cache and notify context
      await caches.delete(CACHE_NAME);
      onCacheExpiredCallback?.();
      return { blobUrl: null, expired: true };
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

    const blob = await response.blob();
    const base64 = await blobToBase64(blob);

    const headers = new Headers({
      'x-cached-at': String(Date.now()),
      'Content-Type': 'application/json',
    });
    await cache.put(
        cacheKey,
        new Response(JSON.stringify({ base64, mimeType: blob.type }), { headers })
    );

    return { blobUrl: base64ToBlob(base64, blob.type), expired: false };

  } catch (error) {
    console.error('Image cache error:', error);
    return null;
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64, mimeType) {
  const byteString = atob(base64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

export async function evictExpiredCache(prefix = null) {
  const cache = await caches.open(CACHE_NAME);
  const keys = await cache.keys();
  const now = Date.now();

  await Promise.all(
      keys
          .filter(request => prefix ? request.url.includes(prefix) : true)
          .map(async (request) => {
            const response = await cache.match(request);
            const cachedAt = response?.headers.get('x-cached-at');
            if (!cachedAt || now - Number(cachedAt) >= CACHE_EXPIRY) {
              await cache.delete(request);
            }
          })
  );
}